import React, { useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  Text,
  Link,
  BlockStack,
  InlineStack,
  Box,
} from '@shopify/polaris';
import { authApi } from '../api/auth-api';
import Logo from '../../../components/logo';

/**
 * ResetPasswordPage - Password reset confirmation page
 *
 * Allows users to set a new password using the reset token received via email.
 * Validates password match and minimum length, then redirects to login on success.
 *
 * @example
 * <Route path="/password/reset/:token" element={<ResetPasswordPage />} />
 */
export default function ResetPasswordPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleEmailChange = useCallback((value) => setEmail(value), []);
  const handlePasswordChange = useCallback((value) => setPassword(value), []);
  const handlePasswordConfirmationChange = useCallback(
    (value) => setPasswordConfirmation(value),
    []
  );

  const handleSubmit = useCallback(async () => {
    setError('');
    setSuccess('');

    if (password !== passwordConfirmation) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      await authApi.resetPassword({
        token,
        email,
        password,
        passwordConfirmation,
      });
      setSuccess('Password has been reset successfully!');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.email?.[0] ||
        err.response?.data?.errors?.password?.[0] ||
        'Unable to reset password. Please try again.';
      setError(message);
      setIsLoading(false);
    }
  }, [token, email, password, passwordConfirmation]);

  return (
    <Box
      background="bg-surface-secondary"
      minHeight="100vh"
      padding={{ xs: '400', md: '800' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 40px)',
        }}
      >
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <BlockStack gap="600">
            <BlockStack gap="200">
              <Logo size="medium" showText align="center" />
              <Text variant="bodyMd" as="p" alignment="center" tone="subdued">
                Set New Password
              </Text>
            </BlockStack>

            <Card>
              <BlockStack gap="400">
                {error && (
                  <Banner tone="critical" onDismiss={() => setError('')}>
                    {error}
                  </Banner>
                )}

                {success && <Banner tone="success">{success}</Banner>}

                <FormLayout>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    autoComplete="email"
                    disabled={isLoading}
                  />

                  <TextField
                    label="New Password"
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    autoComplete="new-password"
                    disabled={isLoading}
                    helpText="Minimum 8 characters"
                  />

                  <TextField
                    label="Confirm Password"
                    type="password"
                    value={passwordConfirmation}
                    onChange={handlePasswordConfirmationChange}
                    autoComplete="new-password"
                    disabled={isLoading}
                  />

                  <Button
                    variant="primary"
                    fullWidth
                    loading={isLoading}
                    onClick={handleSubmit}
                  >
                    Reset Password
                  </Button>
                </FormLayout>

                <InlineStack align="center">
                  <Link url="/login" removeUnderline>
                    ← Back to login
                  </Link>
                </InlineStack>
              </BlockStack>
            </Card>
          </BlockStack>
        </div>
      </div>
    </Box>
  );
}
